import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { withTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@cargomatic/ui';

import CargomaticDatePicker from 'components/v2/CargomaticDatePicker';
import TimePicker from 'components/v2/TimePicker';
import {
  combineDateWithExistingTime,
  combineTimeWithExistingDate,
  formatTimeForInput,
  parseTimeInput,
} from 'utils/formatters/Date';

const getInitialState = (props) => {
  const { stop } = props;
  const { window_start, window_end } = stop;

  const timezone = props.selected_timezone;
  const momentStartDate = window_start
    ? moment.tz(window_start, timezone)
    : null;
  const momentEndDate = window_end ? moment.tz(window_end, timezone) : null;

  const start =
    momentStartDate && momentStartDate.isValid() ? momentStartDate : null;
  const end = momentEndDate && momentEndDate.isValid() ? momentEndDate : null;

  return {
    timezone,
    start,
    end,
    // Raw text mirrored into the TimePicker inputs. The TimePicker is a fully
    // controlled react-input-mask, so its value must echo exactly what the
    // user types — deriving it from `start`/`end` makes incomplete keystrokes
    // ("04:4_ PM") fail to parse and snap the field back, blocking edits.
    startTimeText: formatTimeForInput(start),
    endTimeText: formatTimeForInput(end),
  };
};

class StopAppointmentWindowModal extends Component {
  constructor(props) {
    super(props);
    this.state = getInitialState(props);
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    if (this.props !== newProps) {
      this.setState(getInitialState(newProps));
    }
  }

  handleStartChange = (next) => {
    this.setState({ start: next });
    this.props.onStartChange(next);
  };

  handleEndChange = (next) => {
    this.setState({ end: next });
    this.props.onEndChange(next);
  };

  toCalendarDate = (m) => (m ? new Date(m.year(), m.month(), m.date()) : null);

  renderField(label, value, onChange, testidSuffix, timeText, setTimeText) {
    const { timezone } = this.state;
    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        <div className="flex flex-wrap items-center gap-2">
          <CargomaticDatePicker
            dataTestId={`DatePicker-${testidSuffix}`}
            selected={this.toCalendarDate(value)}
            placeholderText="MM/DD/YYYY"
            isClearable
            onChange={(d) => {
              const combined = combineDateWithExistingTime(d, value, timezone);
              const next = combined ? moment.tz(combined, timezone) : null;
              setTimeText(formatTimeForInput(next));
              onChange(next);
            }}
          />
          <TimePicker
            dataTestId={`TimePicker-${testidSuffix}`}
            value={timeText}
            onChange={(e) => {
              const raw = e.target.value;
              // Always echo the raw input so the controlled mask doesn't fight
              // the user mid-edit. Only push a moment upstream once the time is
              // complete — a partial entry would otherwise round-trip through
              // the parent and reset the field.
              setTimeText(raw);
              if (!parseTimeInput(raw)) return;
              const combined = combineTimeWithExistingDate(
                raw,
                value,
                timezone,
              );
              onChange(moment.tz(combined, timezone));
            }}
          />
        </div>
      </div>
    );
  }

  render() {
    const { t, show, onClose } = this.props;
    const { start, end, timezone } = this.state;
    const tzAbbr = timezone ? moment.tz(timezone).zoneAbbr() : '';

    const handleOpenChange = (open) => {
      if (!open) onClose();
    };

    return (
      <Dialog open={show} onOpenChange={handleOpenChange}>
        <DialogContent
          data-testid="StopAppointmentWindowModal"
          className="max-w-xl max-h-[90vh] flex flex-col p-0 gap-0"
        >
          <DialogHeader className="border-b px-6 py-4 flex-none">
            <DialogTitle>{t('STOP_APPOINTMENT')}</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {this.renderField(
              t('WINDOW_START_WITH_TIMEZONE', { timezone: tzAbbr }),
              start,
              this.handleStartChange,
              'window-start',
              this.state.startTimeText,
              (text) => this.setState({ startTimeText: text }),
            )}
            {this.renderField(
              t('WINDOW_END_WITH_TIMEZONE', { timezone: tzAbbr }),
              end,
              this.handleEndChange,
              'window-end',
              this.state.endTimeText,
              (text) => this.setState({ endTimeText: text }),
            )}
          </div>

          <DialogFooter className="border-t px-6 py-4 flex-none">
            <Button onClick={onClose}>{t('DONE')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
}

StopAppointmentWindowModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onStartChange: PropTypes.func.isRequired,
  onEndChange: PropTypes.func.isRequired,
};

export default withTranslation()(StopAppointmentWindowModal);
